import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AdminPageHeader, DeleteModal, Form, AdminFormAction } from '@ui';
import {
  DEFAULT_OCS_CONSOLE_VALUES,
  NewOcsConsoleForm,
  OcsConsoleForm,
  ocsConsoleFormSchema,
} from '@lp/features/admin/forms/OcsConsoleForm';
import { AuthContext } from '@lp/core/auth';
import { OcsConsoleData } from '@/api/apis/resources';
import { useRemoveOcsConsoleMutation, useUpdateOcsConsoleMutation } from '@/queries/ocs-consoles';
import { useLoaderData } from '@/hooks/useLoaderData';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { getChangedValues } from '@/utils/helpers';
import { useUploadFile } from '@/hooks/useUploadFile';
import { retrieveUnifiedError } from '@/api/utils';
import { AdminPermissions } from '@/permissions';
import { FileType } from '@/queries/file';

export const OcsConsolesEdit = () => {
  const navigate = useNavigate();
  const data = useLoaderData<OcsConsoleData>();
  const { mutateAsync } = useUpdateOcsConsoleMutation();
  const { uploadFiles } = useUploadFile(FileType.Resource);
  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.OcsConsolesCreate);
  const hasEditPermissions = checkPermission(AdminPermissions.OcsConsolesUpdate);
  const hasDeletePermissions = checkPermission(AdminPermissions.OcsConsolesDelete);

  const defaultValues = useMemo<NewOcsConsoleForm>(() => {
    return {
      ...DEFAULT_OCS_CONSOLE_VALUES,
      ocsConsoleName: data.ocsConsoleName,
      serialNumber: data.serialNumber,
      caseName: data.caseName || '',
      nopHub: data.nopHub,
      organType: data.organType,
      status: data.status,
      isInMaintenance: data.isInMaintenance,
      logoPath: data.logoPath ?? undefined,
      trialProgramId: data.trialProgram?.id ?? '',
    };
  }, [data]);

  const title = 'Edit OCS Console';
  const backToListLink = `/admin/devices/ocs_consoles`;

  const handleBackToList = () => {
    navigate(backToListLink);
  };

  const handleSubmit = async (values: NewOcsConsoleForm) => {
    try {
      let uploadedFile;

      if (values.image) {
        uploadedFile = await uploadFiles([values.image]);
      }

      const updateBody = {
        ocsConsoleName: values.ocsConsoleName,
        serialNumber: values.serialNumber,
        caseName: values.caseName,
        nopHub: values.nopHub,
        organType: values.organType,
        status: values.status,
        isInMaintenance: values.isInMaintenance,
        logoPath: uploadedFile ? uploadedFile[0].fileName : values.logoPath,
        trialProgramId: values.trialProgramId || null,
      };

      const changedValues = getChangedValues<Partial<NewOcsConsoleForm>>(updateBody, defaultValues);

      await mutateAsync({
        id: data.id,
        ...changedValues,
      });

      toast.success(`OCS Console updated successfully.`);

      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const { isOpen, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete({
    query: useRemoveOcsConsoleMutation,
    successMessage: 'OCS Console deleted successfully.',
    onDeleteCb: handleBackToList,
  });

  const handleOcsConsoleDelete = () => {
    onModalOpen(data.id);
  };

  return (
    <>
      <Form<NewOcsConsoleForm> defaultValues={defaultValues} schema={ocsConsoleFormSchema} onSubmit={handleSubmit}>
        <div className="flex flex-col gap-9">
          <AdminPageHeader title={title} backToListLink={backToListLink}>
            <AdminFormAction
              type="save"
              showDeleteButton
              handleClick={handleBackToList}
              handleDeleteClick={handleOcsConsoleDelete}
              hasDeletePermissions={hasDeletePermissions}
              hasEditPermissions={hasCreatePermissions || hasEditPermissions}
            />
          </AdminPageHeader>
          <OcsConsoleForm
            type="edit"
            isReadOnly={!hasCreatePermissions && !hasEditPermissions}
            originalOrganType={data.organType}
          />
        </div>
      </Form>
      {hasDeletePermissions && (
        <DeleteModal
          title="Delete OCS Console?"
          description="Are you sure you want to delete the OCS Console?"
          isOpen={isOpen}
          onDelete={onEntityDelete}
          onClose={onModalClose}
        />
      )}
    </>
  );
};
